import { useEffect, useRef, useState } from "react";
import { Download, FileSpreadsheet, FileText, Users, AlertTriangle, BarChart3, Loader2, Database, BookOpen, HardDrive, Layers, RefreshCcw, X, Copy, Activity as ActivityIcon, IndianRupee, Archive, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can, useMyRights } from "../lib/rights";
import { downloadKarvyPreview } from "../lib/karvyPreview";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Items inside "Modified Files" that operate on per-client data (PAN-scoped on
// the backend) and are useful to associates for their mapped clients.
// Items NOT in this list (e.g. scheme_master, nav_master, subbroker-master)
// are global / firm-level — they stay hidden for sub-brokers.
const SUB_BROKER_MODIFIED_IDS = new Set([
  "investor-master",
  "aum-master",
  "kyc-missing",
  "kyc-mismatch",
  "transaction-master",
  "active-units",
  "client-master-export",
]);

const getDownloadToken = async () => {
  const token = localStorage.getItem("token");
  const resp = await fetch(`${API_URL}/api/database/download-token`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error("Failed to get download token");
  const data = await resp.json();
  return data.token;
};

const rawFiles = [
  {
    id: "bse-scheme-master",
    label: "BSE Scheme Master",
    dbName: "bse_scheme_raw",
    description: "All 43 columns as fetched from BSE StarMF",
    icon: Database,
    iconBg: "bg-indigo-500/10 text-indigo-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=bse_scheme_raw&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "scheme-data",
    label: "Scheme Data (AMFI)",
    dbName: "scheme_data",
    description: "AMFI scheme data with categories, NAV names & ISINs",
    icon: BookOpen,
    iconBg: "bg-orange-500/10 text-orange-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=scheme_data&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "mfsd201",
    label: "MFSD201 (Karvy)",
    dbName: "MFSD201",
    description: "Transaction Details",
    icon: HardDrive,
    iconBg: "bg-teal-500/10 text-teal-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=MFSD201&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "mfsd211",
    label: "MFSD211 (Karvy)",
    dbName: "MFSD211",
    description: "Investor Master",
    icon: HardDrive,
    iconBg: "bg-teal-500/10 text-teal-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=MFSD211&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "mfsd203",
    label: "MFSD203 (Karvy)",
    dbName: "MFSD203",
    description: "Balance Units / AUM",
    icon: HardDrive,
    iconBg: "bg-teal-500/10 text-teal-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=MFSD203&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "wbr9",
    label: "WBR9 (CAMS)",
    dbName: "WBR9",
    description: "Investor Master & Balance Units",
    icon: HardDrive,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=WBR9&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "wbr9d",
    label: "WBR9D (CAMS)",
    dbName: "WBR9D",
    description: "Investor Master (Historical)",
    icon: HardDrive,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=WBR9D&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "wbr2",
    label: "WBR2 (CAMS)",
    dbName: "WBR2",
    description: "Transaction Details",
    icon: HardDrive,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=WBR2&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "wbr2a",
    label: "WBR2A (CAMS)",
    dbName: "WBR2A",
    description: "Historical Transactions for Transferred-In Folios",
    icon: HardDrive,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=WBR2A&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "wbr77-raw",
    label: "WBR77_Raw (CAMS Commission — raw ingest)",
    dbName: "WBR77_Raw",
    description: "Untouched WBR77 rows exactly as received from CAMS (pre-enrichment).",
    icon: HardDrive,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=WBR77_Raw&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "mfsd205-raw",
    label: "MFSD205_Raw (KARVY — raw ingest)",
    dbName: "MFSD205_Raw",
    description: "Untouched MFSD205 rows exactly as received from KFintech (pre-SubBroker resolution).",
    icon: HardDrive,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=MFSD205_Raw&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "volt-raw",
    label: "VOLT_Raw (VOLT — raw ingest)",
    dbName: "VOLT_Raw",
    description: "Untouched VOLTMONEY commission rows exactly as received.",
    icon: HardDrive,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=VOLT_Raw&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "scheme-master-pulse-labs",
    label: "Scheme Master - Pulse Labs",
    dbName: "scheme_master_pulse_labs",
    description: "Scheme Code to Product Code mappings with asset classification",
    icon: Database,
    iconBg: "bg-amber-500/10 text-amber-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=scheme_master_pulse_labs&token=${downloadToken}`, "_blank");
    },
  },
];

const modifiedFiles = [
  {
    id: "wbr77",
    label: "WBR77 (CAMS Commission — enriched)",
    dbName: "WBR77",
    description: "Post-processed WBR77 with resolved SubBroker + Kinntegra Share columns. Rebuilt from WBR77_Raw on every commission run.",
    source: "WBR77_Raw + subbroker_mapping + investor_master (PAN & name chain)",
    icon: HardDrive,
    iconBg: "bg-amber-500/10 text-amber-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=WBR77&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "mfsd205",
    label: "MFSD205 (KARVY Commission — enriched)",
    dbName: "MFSD205",
    description: "Post-processed MFSD205 with resolved SubBroker + derived commission columns. Rebuilt from MFSD205_Raw on every commission run.",
    source: "MFSD205_Raw + KFintech ARN → internal SubBroker code map",
    icon: HardDrive,
    iconBg: "bg-amber-500/10 text-amber-400",
    // usePreviewModal=true routes through the KARVY preview modal so
    // unmapped Brokerage_Types can be tagged before the workbook is
    // generated. Retained here so the "MFSD205 Preview" workflow keeps
    // working on this modified-files card.
    usePreviewModal: "karvy_preview",
  },
  {
    id: "voltmoney",
    label: "VOLTMONEY (VOLT Commission — enriched)",
    dbName: "VOLTMONEY",
    description: "Post-processed VOLT commission rows with resolved IFA + Kinntegra Share. Rebuilt from VOLT_Raw on every commission run.",
    source: "VOLT_Raw + volt_associate_map (IFA overrides)",
    icon: HardDrive,
    iconBg: "bg-amber-500/10 text-amber-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=VOLTMONEY&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "scheme-master",
    label: "Scheme Master",
    dbName: "scheme_master",
    description: "Includes Launch Date, Closure Date, Face Value. ISINs verified in AMFI data.",
    source: "BSE Scheme Raw filtered by AMFI NAV availability",
    icon: Layers,
    iconBg: "bg-emerald-500/10 text-emerald-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/database/export/excel?collections=scheme_master&token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "nav-master",
    label: "NAV Master",
    dbName: "nav_master",
    description: "Last 5 days NAV, 3Y NAV, 5Y NAV, 31-Jan-2018 NAV",
    source: "AMFI Historical NAV API",
    icon: BarChart3,
    iconBg: "bg-amber-500/10 text-amber-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/nav-master/export/excel?token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "investor-master",
    label: "Investor Master",
    dbName: "investor_master",
    description: "Consolidated CAMS & KARVY investor data",
    source: "CAMS WBR9 + Karvy MFSD211",
    icon: Users,
    iconBg: "bg-teal-500/10 text-teal-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/common-investor-master/export/excel?token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "aum-master",
    label: "AUM Master",
    dbName: "aum_master",
    description: "NAV, market values, segregated & unclaimed tabs",
    source: "CAMS WBR9 + WBR9D + Karvy MFSD211 + MFSD203",
    icon: BarChart3,
    iconBg: "bg-violet-500/10 text-violet-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/aum-master/export/excel?token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "kyc-missing",
    label: "KYC Missing Report",
    dbName: "kyc_missing",
    description: "Folios with missing PAN, Mobile, Email, DOB, Pincode, IFSC",
    source: "Investor Master + AUM Master + Scheme Master",
    icon: AlertTriangle,
    iconBg: "bg-amber-500/10 text-amber-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/reconciliation/kyc-missing/export/excel?token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "kyc-mismatch",
    label: "KYC Mismatch Report",
    dbName: "kyc_mismatch",
    description: "PAN-level mismatch cases — Name, DOB, Email, Phone, Address",
    source: "Investor Master + AUM Master + Scheme Master",
    icon: AlertTriangle,
    iconBg: "bg-rose-500/10 text-rose-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/reconciliation/kyc-mismatch/pan/export/excel?token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "family-mapping",
    label: "Family Mapping",
    dbName: "family_mapping",
    description: "PAN ↔ Group Head linkage powering family-portfolio views",
    source: "Masters → Family Mapping Upload",
    icon: Users,
    iconBg: "bg-amber-500/10 text-amber-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/family-mapping/export?token=${downloadToken}`, "_blank");
    },
  },
  {
    id: "transaction-master",
    label: "Transaction Master",
    dbName: "transaction_master",
    description: "Unified transaction data — 17 standardized columns",
    source: "CAMS WBR2 + WBR2A + Karvy MFSD201",
    icon: RefreshCcw,
    iconBg: "bg-emerald-500/10 text-emerald-400",
    hasGenerate: true,
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/transaction-master/export/excel?token=${downloadToken}`, "_blank");
    },
    generateAction: async () => {
      const token = localStorage.getItem("token");
      // Full rebuild from raw sources (WBR2 + WBR2A + MFSD201). Also
      // auto-chains the download ZIP cache refresh — so once this job
      // completes, the Download button serves the freshly-rebuilt file.
      const resp = await fetch(`${API_URL}/api/transaction-master/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return { success: false, detail: err.detail || "Rebuild failed" };
      }
      const data = await resp.json();
      return { success: true, data };
    },
  },
  {
    id: "transfers",
    label: "Transfers",
    dbName: "transfers",
    description: "All transactions for every folio+scheme that has ever had a 'Transfer' entry — complete history, auto-refreshed after each Transaction Master rebuild",
    source: "Auto-derived from transaction_master",
    icon: RefreshCcw,
    iconBg: "bg-blue-500/10 text-blue-400",
    hasGenerate: true,
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/transfers/export/excel?token=${downloadToken}`, "_blank");
    },
    generateAction: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/transfers/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return { success: false, detail: err.detail || "Rebuild failed" };
      }
      const data = await resp.json();
      return { success: true, data };
    },
  },
  {
    id: "gifts",
    label: "Gifts",
    dbName: "gifts",
    description: "All TIG2/TIG3/TOG2/TOG3 entries — MF Gift In & Gift Out transactions. Auto-refreshed after each Transaction Master rebuild. Two sheets: Gift In (TIG2/TIG3) and Gift Out (TOG2/TOG3).",
    source: "Auto-derived from transaction_master",
    icon: RefreshCcw,
    iconBg: "bg-pink-500/10 text-pink-400",
    hasGenerate: true,
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/gifts/export/excel?token=${downloadToken}`, "_blank");
    },
    generateAction: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/gifts/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return { success: false, detail: err.detail || "Rebuild failed" };
      }
      const data = await resp.json();
      return { success: true, data };
    },
  },
  {
    id: "transmissions",
    label: "Transmissions",
    dbName: "transmissions",
    description: "All Transmission In / Transmission Out transactions (folio transferred due to death of holder). Auto-refreshed after each Transaction Master rebuild. Two sheets: Transmission In and Transmission Out.",
    source: "Auto-derived from transaction_master",
    icon: RefreshCcw,
    iconBg: "bg-purple-500/10 text-purple-400",
    hasGenerate: true,
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/transmissions/export/excel?token=${downloadToken}`, "_blank");
    },
    generateAction: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/transmissions/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return { success: false, detail: err.detail || "Rebuild failed" };
      }
      const data = await resp.json();
      return { success: true, data };
    },
  },
  {
    id: "multi_broker_cases",
    label: "Multi-Broker Cases",
    dbName: "multi_broker_cases",
    description: "Folios where AUM Master units don't match Transaction Master computed balance — potential multi-broker cases. Excludes folios with SIP/STP/SWP activity in the last 7 days (pending settlement).",
    source: "Auto-derived from aum_master vs transaction_master",
    icon: RefreshCcw,
    iconBg: "bg-amber-500/10 text-amber-500",
    hasGenerate: true,
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/multi-broker/export/excel?token=${downloadToken}`, "_blank");
    },
    generateAction: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/multi-broker/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return { success: false, detail: err.detail || "Rebuild failed" };
      }
      const data = await resp.json();
      return { success: true, data };
    },
  },
  {
    id: "client-master-export",
    label: "Client Master",
    dbName: "client_master",
    description: "Client list — PAN, Name, Guardian PAN, mapped Associate Code",
    source: "AUM Master ⋈ Sub Broker Mapping (live)",
    icon: FileSpreadsheet,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/subbroker/client-master/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        if (resp.status === 403) throw new Error("Super-admin only");
        throw new Error("Export failed");
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `client_master_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
  {
    id: "subbroker-master-export",
    label: "Associate Master (Live Export)",
    dbName: "associate_master",
    description: "Current associate master — 100 Associate Master columns from the latest associate details, plus status, introducer, commercials and documents",
    source: "Associate (Sub Broker) collection (live)",
    icon: FileSpreadsheet,
    iconBg: "bg-teal-500/10 text-teal-400",
    action: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/subbroker/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        if (resp.status === 403) throw new Error("Super-admin only");
        throw new Error("Export failed");
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `associate_master_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
];

const mappingGuides = [
  {
    id: "column-mapping",
    label: "Investor Master Mapping Guide",
    dbName: "column_mapping_guide",
    description: "Column mapping reference for building Investor Master",
    icon: FileSpreadsheet,
    iconBg: "bg-sky-500/10 text-sky-400",
    action: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/column-mapping/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const disposition = resp.headers.get("content-disposition");
      let filename = "Column Mapping Guide.xlsx";
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
  {
    id: "txn-master-mapping",
    label: "Transaction Master Mapping Guide",
    dbName: "txn_master_mapping_guide",
    description: "Column mapping template for building Transaction Master",
    icon: FileSpreadsheet,
    iconBg: "bg-emerald-500/10 text-emerald-400",
    action: async () => {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/transaction-master/template/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Transaction_Master_Mapping_Guide.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
  {
    id: "unique-flags-txntype-source",
    label: "Unique Flags, Txn Types & Sources",
    dbName: "flag_mapping_config",
    description: "Live CAMS + Karvy programmatic mapping rules (txn_type, txn_flag, flag, description, treatment)",
    icon: FileSpreadsheet,
    iconBg: "bg-amber-500/10 text-amber-400",
    action: async () => {
      const downloadToken = await getDownloadToken();
      window.open(`${API_URL}/api/mapping-rules/download?token=${downloadToken}`, "_blank", "noopener");
    },
  },
];

// Format an ISO timestamp as a compact "N units ago" string. Returns null if
// the value is missing/unparseable so the caller can drop the badge entirely.
const formatRelativeTime = (iso) => {
  if (!iso) return null;
  let d;
  try {
    d = typeof iso === "string" ? new Date(iso) : iso;
    if (!d || isNaN(d.getTime())) return null;
  } catch {
    return null;
  }
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return "just now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr}y ago`;
};

const DownloadCard = ({ item, downloading, onDownload, onGenerate, rowCount, lastUpdated, activeUnitsStatus, tmRebuildStatus, workerHealth }) => {
  const isLoading = downloading[item.id];
  const isGenerating = downloading[`${item.id}-generate`];
  // ── 2026-07-03 fallback ────────────────────────────────────────────
  // /api/database/collections-info can time out at Cloudflare (~100s)
  // during a live active_units bulk rebuild (20 workers hammering
  // Atlas). When that happens, `rowCount` stays as "loading" or null
  // and the user sees NO count on the Active Units card. But the
  // active-units status endpoint (polled every 3s during a rebuild)
  // already returns `rows_in_collection` — use it as a fallback so
  // the count is always visible.
  const effectiveRowCount = (() => {
    if (typeof rowCount === "number") return rowCount;
    if (item.id === "active-units" && activeUnitsStatus?.rows_in_collection != null) {
      return activeUnitsStatus.rows_in_collection;
    }
    return rowCount;
  })();
  const formattedCount =
    typeof effectiveRowCount === "number"
      ? effectiveRowCount.toLocaleString("en-IN")
      : effectiveRowCount === "loading"
        ? "…"
        : null;
  const relativeUpdated = formatRelativeTime(lastUpdated);
  const absoluteUpdated = lastUpdated
    ? (() => {
        try {
          return new Date(lastUpdated).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          });
        } catch {
          return lastUpdated;
        }
      })()
    : null;
  return (
    <div className="flex flex-col gap-1 min-w-0 w-full">
    <div className="flex items-center gap-2 min-w-0 w-full">
      <button
        data-testid={`download-${item.id}`}
        onClick={() => onDownload(item)}
        disabled={isLoading}
        className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-all hover:shadow-md hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed flex-1 min-w-0"
      >
        <div className={`rounded-lg p-2 ${item.iconBg} flex-shrink-0`}>
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <item.icon className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 text-sm truncate">
            {item.label}
            {item.dbName && (
              <span className="ml-2 text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{item.dbName}</span>
            )}
            {formattedCount !== null && (
              <span
                className="ml-2 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded"
                data-testid={`row-count-${item.id}`}
                title="Total rows in this collection"
              >
                {formattedCount} rows
              </span>
            )}
            {relativeUpdated && (
              <span
                className="ml-1.5 text-[10px] font-medium text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded"
                data-testid={`last-updated-${item.id}`}
                title={absoluteUpdated ? `Last updated: ${absoluteUpdated}` : "Last updated"}
              >
                updated {relativeUpdated}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">{item.description}</div>
          <div className="text-[10px] text-gray-400 mt-0.5 truncate h-[14px]">{item.source ? `Source: ${item.source}` : "\u00A0"}</div>
        </div>
        <Download className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
      </button>
      {item.hasGenerate ? (
        <button
          data-testid={`generate-${item.id}`}
          onClick={() => onGenerate(item)}
          disabled={isGenerating}
          className="flex items-center justify-center w-9 h-9 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-60 flex-shrink-0"
          title="Generate / Regenerate"
        >
          {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </button>
      ) : (
        <div className="w-9 h-9 flex-shrink-0" aria-hidden="true" />
      )}
    </div>
    {/* Inline progress bar — only for the active_units card while a rebuild is running */}
    {item.id === "active-units" && activeUnitsStatus && activeUnitsStatus.status === "running" && (() => {
      const done = activeUnitsStatus.progress_done || 0;
      const total = activeUnitsStatus.progress_total || 0;
      const rowsInDb = activeUnitsStatus.rows_in_collection || 0;
      const stage = activeUnitsStatus.stage || "";
      const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

      // Human-readable stage label so the user never sees "0/0 preparing..."
      // without context during the multi-second compute_unified / drop / xlsx phases.
      const STAGE_LABELS = {
        initializing: "Initializing…",
        computing_client_list: "Computing client list…",
        building_folio_map: "Building folio map…",
        dropping_old_rows: "Clearing old snapshot…",
        classifying: "Classifying clients",
        building_indexes: "Building indexes…",
        building_excel: "Generating Excel…",
      };
      const stageLabel = STAGE_LABELS[stage] || (total === 0 ? "Preparing…" : "Classifying clients");

      let etaText = stageLabel;
      try {
        const started = activeUnitsStatus.started_at;
        if (started && done > 0 && total > done) {
          // Backend returns ISO with no TZ suffix (naive UTC). If Z/±HH:MM
          // is absent, treat as UTC — otherwise the browser parses it in
          // local time and elapsed drifts by the local TZ offset (e.g.
          // IST +5:30 made a 3-min-old rebuild look 5.7 h old, blowing
          // the ETA up to ~82k minutes).
          const startedNormalized = /Z|[+-]\d{2}:?\d{2}$/.test(started)
            ? started
            : `${started}Z`;
          const startedMs = new Date(startedNormalized).getTime();
          const elapsed = (Date.now() - startedMs) / 1000;
          if (elapsed > 0) {
            const rate = done / elapsed; // clients per sec
            const remaining = (total - done) / rate;
            const mins = Math.floor(remaining / 60);
            const secs = Math.round(remaining % 60);
            etaText = mins > 0 ? `~${mins}m ${secs}s remaining` : `~${secs}s remaining`;
          }
        } else if (total > 0 && done >= total) {
          etaText = "finalising…";
        }
      } catch (_e) { /* ignore */ }

      // Headline line varies by phase:
      //  • classify phase (total > 0) → "Rebuilding · 65% · 1,700/2,607 clients"
      //  • pre-classify phase (total == 0) → "Rebuilding · <stage label> · X rows so far"
      const headline = total > 0
        ? `Rebuilding · ${pct}% · ${done.toLocaleString()} / ${total.toLocaleString()} clients`
        : `Rebuilding · ${stageLabel}${rowsInDb > 0 ? ` · ${rowsInDb.toLocaleString()} rows so far` : ""}`;

      return (
        <div className="ml-2 mr-11 mt-1 mb-1" data-testid="active-units-downloads-progress">
          <div className="flex items-center justify-between text-[10px] mb-0.5">
            <span className="text-fuchsia-700 font-semibold">
              {headline}
            </span>
            <span className="text-gray-500">{etaText}</span>
          </div>
          <div className="h-1.5 w-full bg-fuchsia-100 rounded-full overflow-hidden">
            <div
              className={`h-full bg-fuchsia-600 ${total === 0 ? "animate-pulse" : "transition-all duration-500 ease-out"}`}
              style={{ width: total > 0 ? `${pct}%` : "100%" }}
            />
          </div>
        </div>
      );
    })()}
    {item.id === "active-units" && activeUnitsStatus && activeUnitsStatus.status === "completed" && activeUnitsStatus.completed_at && (
      <div className="ml-2 mt-0.5 text-[10px] text-emerald-700" data-testid="active-units-downloads-last-rebuild">
        Last rebuilt {new Date(activeUnitsStatus.completed_at).toLocaleString()} · {(activeUnitsStatus.rows_in_collection || 0).toLocaleString()} rows
      </div>
    )}
    {/* Inline progress bar — transaction_master rebuild. Same pattern as
        active_units above. Progress % is derived from
        live_count / expected_total (backup_count is the pre-rebuild
        snapshot, captured by _rebuild_transaction_master before it drops
        the live collection — so it's the authoritative "how many rows
        were there before we started" number). */}
    {item.id === "transaction-master" && tmRebuildStatus && tmRebuildStatus.is_running && (() => {
      const live = tmRebuildStatus.live_count || 0;
      const expected = tmRebuildStatus.expected_total || 0;
      const phase = tmRebuildStatus.phase || "";
      const pct = expected > 0 ? Math.min(100, Math.round((live / expected) * 100)) : 0;

      const PHASE_LABELS = {
        snapshotting_backup: "Snapshotting existing TM…",
        reading_raw_and_writing_tm: "Reading raw sources + writing TM",
        chaining_caches: "Refreshing dashboard & ZIP caches…",
        done: "Finalising…",
      };
      const phaseLabel = PHASE_LABELS[phase] || (expected === 0 ? "Preparing…" : "Rebuilding");

      let etaText = phaseLabel;
      try {
        const started = tmRebuildStatus.started_at;
        if (started && live > 0 && expected > live && phase === "reading_raw_and_writing_tm") {
          const startedNormalized = /Z|[+-]\d{2}:?\d{2}$/.test(started) ? started : `${started}Z`;
          const startedMs = new Date(startedNormalized).getTime();
          const elapsed = (Date.now() - startedMs) / 1000;
          if (elapsed > 0) {
            const rate = live / elapsed;
            const remaining = (expected - live) / rate;
            const mins = Math.floor(remaining / 60);
            const secs = Math.round(remaining % 60);
            etaText = mins > 0 ? `~${mins}m ${secs}s remaining` : `~${secs}s remaining`;
          }
        } else if (phase === "reading_raw_and_writing_tm" && expected > 0 && live >= expected) {
          // Insert step is done but the inner rebuild is still deriving
          // txn_flag / treatment / mapping columns before the phase flips.
          etaText = "Applying mapping rules…";
        } else if (phase === "chaining_caches" || phase === "done") {
          etaText = "finalising…";
        }
      } catch (_e) { /* ignore */ }

      const headline = expected > 0
        ? `Rebuilding · ${pct}% · ${live.toLocaleString()} / ${expected.toLocaleString()} rows`
        : `Rebuilding · ${phaseLabel}${live > 0 ? ` · ${live.toLocaleString()} rows so far` : ""}`;

      return (
        <div className="ml-2 mr-11 mt-1 mb-1" data-testid="transaction-master-downloads-progress">
          <div className="flex items-center justify-between text-[10px] mb-0.5">
            <span className="text-emerald-700 font-semibold">{headline}</span>
            <span className="text-gray-500">{etaText}</span>
          </div>
          <div className="h-1.5 w-full bg-emerald-100 rounded-full overflow-hidden">
            <div
              className={`h-full bg-emerald-600 ${expected === 0 ? "animate-pulse" : "transition-all duration-500 ease-out"}`}
              style={{ width: expected > 0 ? `${pct}%` : "100%" }}
            />
          </div>
        </div>
      );
    })()}
    {item.id === "transaction-master" && tmRebuildStatus && tmRebuildStatus.status === "failed" && tmRebuildStatus.error && (
      <div className="ml-2 mt-0.5 text-[10px] text-red-700" data-testid="transaction-master-downloads-last-error" title={tmRebuildStatus.error}>
        Last rebuild failed: {String(tmRebuildStatus.error).slice(0, 80)}
      </div>
    )}
    {/* Worker Health — surfaces crashes from the bulk PAN-sweep asyncio
        workers (Motor pool exhaustion, per-PAN exceptions, task
        cancellations). Empty state renders as a tiny green pill; when
        24h errors > 0 it becomes an amber expandable list of the last 5. */}
    {item.id === "active-units" && workerHealth && (() => {
      const c24 = workerHealth?.counts?.last_24h?.total || 0;
      const c7d = workerHealth?.counts?.last_7d?.total || 0;
      const live = workerHealth?.live_workers_this_pod || 0;
      const errs = workerHealth?.recent_errors || [];
      const bad = c24 > 0;

      const _authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      });

      const _clearStuckLock = async () => {
        const ok = window.confirm(
          "This will delete the active_units_rebuild_meta lock document AND wipe the active_units_rebuild_queue.\n\n" +
          "Use only when a rebuild is wedged (queue not draining, workers idle, meta doc frozen).\n\n" +
          "Safe to call as many times as needed. Continue?"
        );
        if (!ok) return;
        try {
          const resp = await fetch(
            `${API_URL}/api/active-units/rebuild-meta/clear?also_reset_queue=true`,
            { method: "POST", headers: _authHeaders() }
          );
          const data = await resp.json();
          if (!resp.ok) throw new Error(data?.detail || "Clear failed");
          toast.success("Stuck lock cleared", {
            description: `meta_deleted=${data.meta_deleted}, queue_deleted=${data.queue_deleted}. Ready to trigger a fresh rebuild.`,
          });
        } catch (e) {
          toast.error("Clear failed", { description: String(e?.message || e) });
        }
      };

      const _fullRecovery = async () => {
        const ok = window.confirm(
          "Run the FULL active_units recovery sequence?\n\n" +
          "1) Clear the stuck rebuild-meta lock + wipe the rebuild queue\n" +
          "2) Turn ON the bulk-sweep flag (Option-B toggle)\n" +
          "3) Trigger a fresh rebuild from investor_master (force_reset=true)\n" +
          "4) Refresh the Dashboard cache\n" +
          "5) Turn OFF the bulk-sweep flag (back to Option-B default)\n\n" +
          "Step 3 kicks off a 30-60 min background job on production. Continue?"
        );
        if (!ok) return;
        const stepToast = toast.loading("Step 1/5: Clearing stuck lock…");
        try {
          // Step 1: clear meta + reset queue
          let r = await fetch(
            `${API_URL}/api/active-units/rebuild-meta/clear?also_reset_queue=true`,
            { method: "POST", headers: _authHeaders() }
          );
          let d = await r.json();
          if (!r.ok) throw new Error(d?.detail || "Step 1 (clear) failed");
          toast.loading("Step 2/5: Enabling bulk sweep…", { id: stepToast });

          // Step 2: enable bulk sweep
          r = await fetch(
            `${API_URL}/api/active-units/bulk-rebuild/toggle`,
            { method: "POST", headers: _authHeaders(), body: JSON.stringify({ enabled: true }) }
          );
          d = await r.json();
          if (!r.ok) throw new Error(d?.detail || "Step 2 (toggle on) failed");
          toast.loading("Step 3/5: Triggering rebuild…", { id: stepToast });

          // Step 3: trigger rebuild
          r = await fetch(
            `${API_URL}/api/active-units/rebuild-all-pans-sequential?source=investor_master&force_reset=true&override_lease=true`,
            { method: "POST", headers: _authHeaders() }
          );
          d = await r.json();
          if (!r.ok) throw new Error(d?.detail || "Step 3 (trigger) failed");
          const totalPans = d?.total_pans || d?.pending_pans || 0;
          toast.success(
            `Rebuild queued for ${totalPans} PANs`,
            {
              id: stepToast,
              description:
                "Runs in background (30-60 min). Poll GET /api/active-units/rebuild-all-pans-sequential/status. Refresh Dashboard cache manually when done, then click 'Disable Bulk Sweep' to return to Option-B.",
              duration: 12000,
            }
          );
        } catch (e) {
          toast.error("Recovery aborted", { id: stepToast, description: String(e?.message || e) });
        }
      };

      const _disableBulkSweep = async () => {
        try {
          const r = await fetch(
            `${API_URL}/api/active-units/bulk-rebuild/toggle`,
            { method: "POST", headers: _authHeaders(), body: JSON.stringify({ enabled: false }) }
          );
          const d = await r.json();
          if (!r.ok) throw new Error(d?.detail || "Disable failed");
          toast.success("Bulk sweep disabled (Option-B default restored)");
        } catch (e) {
          toast.error("Disable failed", { description: String(e?.message || e) });
        }
      };

      return (
        <div className="ml-2 mr-11 mt-1" data-testid="worker-health-widget">
          <details className="group">
            <summary
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium text-[10px] cursor-pointer select-none ${
                bad
                  ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                  : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              }`}
              data-testid="worker-health-summary"
            >
              <ActivityIcon className="w-3 h-3" />
              <span>
                Workers: {bad ? `⚠ ${c24} error${c24 === 1 ? "" : "s"} · 24h` : "healthy · 0 errors 24h"}
              </span>
              <span className="text-[9px] text-gray-500 font-normal">
                · 7d: {c7d} · live: {live}
              </span>
            </summary>
            {/* Recovery buttons — surface when the rebuild pipeline gets wedged. */}
            <div className="mt-1.5 mb-1 flex flex-wrap items-center gap-1.5" data-testid="rebuild-recovery-actions">
              <button
                type="button"
                onClick={_clearStuckLock}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[10px] font-medium"
                data-testid="clear-stuck-lock-btn"
                title="Delete active_units_rebuild_meta lock + wipe rebuild queue. Idempotent."
              >
                Clear Stuck Lock
              </button>
              <button
                type="button"
                onClick={_fullRecovery}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 text-[10px] font-medium"
                data-testid="full-recovery-btn"
                title="One-click: clear lock → enable bulk sweep → trigger rebuild"
              >
                Run Full Recovery
              </button>
              <button
                type="button"
                onClick={_disableBulkSweep}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700 text-[10px] font-medium"
                data-testid="disable-bulk-sweep-btn"
                title="Turn off the Option-B bulk sweep flag after a manual rebuild completes"
              >
                Disable Bulk Sweep
              </button>
            </div>
            {errs.length > 0 ? (
              <div className="mt-1 border border-gray-200 rounded-md bg-white shadow-sm overflow-hidden" data-testid="worker-health-details">
                <table className="w-full text-[10px]">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-2 py-1 text-left">When</th>
                      <th className="px-2 py-1 text-left">Kind</th>
                      <th className="px-2 py-1 text-left">PAN</th>
                      <th className="px-2 py-1 text-left">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errs.map((e, i) => (
                      <tr key={`${e.at}-${i}`} className="border-t border-gray-100">
                        <td className="px-2 py-1 text-gray-500 whitespace-nowrap">
                          {e.at ? new Date(e.at.endsWith("Z") ? e.at : `${e.at}Z`).toLocaleString() : "—"}
                        </td>
                        <td className="px-2 py-1">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${
                            e.kind === "per_pan" ? "bg-blue-50 text-blue-700"
                            : e.kind === "task_crash" ? "bg-red-50 text-red-700"
                            : e.kind === "task_cancelled" ? "bg-gray-100 text-gray-700"
                            : "bg-purple-50 text-purple-700"
                          }`}>{e.kind}</span>
                        </td>
                        <td className="px-2 py-1 font-mono text-gray-700">{e.pan || "—"}</td>
                        <td className="px-2 py-1 text-red-700 break-all">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-2 py-1 bg-gray-50 border-t border-gray-100 text-[9px] text-gray-500">
                  Showing latest {errs.length}. Rolling log capped at 500 rows in <code>active_units_worker_errors</code>.
                </div>
              </div>
            ) : (
              <div className="mt-1 text-[10px] text-gray-500" data-testid="worker-health-empty">
                No worker errors recorded yet. Populated automatically when a bulk-rebuild worker task crashes.
              </div>
            )}
          </details>
        </div>
      );
    })()}
    </div>
  );
};

export default function Downloads() {
  const { user } = useAuth();
  const isSubBroker = user?.role === "sub_broker";
  const myRights = useMyRights(user);
  const [downloading, setDownloading] = useState({});
  const [rulesPreview, setRulesPreview] = useState(null); // { rows: [...] } when open
  // KARVY Brokerage_Type gap-fill modal state.
  //   status: idle | loading | gaps | saving | downloading
  //   gaps  : [{brokerage_type, row_count, bucket}]
  const [karvyModal, setKarvyModal] = useState({ open: false, status: "idle", gaps: [] });
  const [rowCounts, setRowCounts] = useState({}); // { dbName: number | "loading" }
  const [lastUpdatedMap, setLastUpdatedMap] = useState({}); // { dbName: iso | null }
  const [myCommissionFiles, setMyCommissionFiles] = useState(null); // null = loading, [] = none
  const [downloadingComm, setDownloadingComm] = useState({}); // { job_id: bool }
  // Live status of the active_units rebuild — polled from /api/active-units/status
  // so the user gets a visible progress bar + ETA even when they navigated away
  // and came back.
  const [activeUnitsStatus, setActiveUnitsStatus] = useState(null);
  // Live status of the transaction_master rebuild — polled from
  // /api/transaction-master/rebuild-status. Same pattern as activeUnitsStatus
  // so the Downloads card shows an inline progress bar while a rebuild is
  // running, and re-hydrates correctly if the user navigates away/back.
  const [tmRebuildStatus, setTmRebuildStatus] = useState(null);
  // Worker Health for the bulk PAN-sweep rebuild — surfaces crashes
  // from asyncio workers so the operator doesn't have to shell into
  // the pod. Polled independently of the rebuild status so a stale
  // amber pill from yesterday remains visible even when no rebuild is
  // currently running.
  const [workerHealth, setWorkerHealth] = useState(null);
  const rowCountsFetched = useRef(false);
  const commFetched = useRef(false);

  // Poll /api/active-units/status while a rebuild is in progress.
  // Always do one fetch on mount so a rebuild kicked off elsewhere shows up.
  useEffect(() => {
    const token = localStorage.getItem("token");
    let timer = null;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/active-units/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setActiveUnitsStatus(j);
        if (j.status === "running") {
          timer = setTimeout(poll, 3000);
        } else {
          // Re-poll less often after completion in case another rebuild kicks off.
          timer = setTimeout(poll, 15000);
        }
      } catch (_e) {
        if (!cancelled) timer = setTimeout(poll, 6000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Poll /api/transaction-master/rebuild-status. Fast poll (3 s) while a
  // rebuild is running, slow poll (30 s) otherwise so cross-tab kicks still
  // show up on this page.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    let timer = null;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/transaction-master/rebuild-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancelled) {
          const j = await r.json();
          setTmRebuildStatus(j);
          timer = setTimeout(poll, j.is_running ? 3000 : 30000);
          return;
        }
      } catch (_e) { /* ignore */ }
      if (!cancelled) timer = setTimeout(poll, 10000);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Poll /api/active-units/worker-health every 20s. Admin-only endpoint;
  // non-admin users just get a 403 and the widget stays hidden.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    let timer = null;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/active-units/worker-health?limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancelled) setWorkerHealth(await r.json());
      } catch (_e) { /* ignore */ }
      if (!cancelled) timer = setTimeout(poll, 20000);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (commFetched.current) return;
    commFetched.current = true;
    const token = localStorage.getItem("token");
    fetch(`${API_URL}/api/commissions/my-files`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { files: [] }))
      .then((data) => {
        setMyCommissionFiles(Array.isArray(data?.files) ? data.files : []);
      })
      .catch(() => setMyCommissionFiles([]));
  }, []);

  const downloadCommissionFile = async (jobId, period) => {
    setDownloadingComm((p) => ({ ...p, [jobId]: true }));
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/commissions/my-download/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(body || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `commissions_${period || jobId}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname} — open with your sub-broker code as password`);
    } catch (e) {
      toast.error(`Download failed: ${String(e).slice(0, 160)}`);
    } finally {
      setDownloadingComm((p) => ({ ...p, [jobId]: false }));
    }
  };

  useEffect(() => {
    if (rowCountsFetched.current) return;
    rowCountsFetched.current = true;
    // Mark all known dbNames as "loading" so the UI shows a spinner immediately
    const allItems = [...rawFiles, ...modifiedFiles];
    const initial = {};
    allItems.forEach((it) => {
      if (it.dbName) initial[it.dbName] = "loading";
    });
    setRowCounts(initial);

    const token = localStorage.getItem("token");
    fetch(`${API_URL}/api/database/collections-info?with_timestamps=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const counts = data?.collections || {};
        const stamps = data?.last_updated_at || {};
        // Resolve loading → actual number AND surface every OTHER collection
        // returned by the endpoint so the "Other Collections" section on the
        // page can render them (must be seeded into rowCounts, not filtered
        // by the initial loading map).
        setRowCounts(() => {
          const next = {};
          Object.entries(counts).forEach(([k, v]) => {
            next[k] = typeof v === "number" ? v : 0;
          });
          return next;
        });
        setLastUpdatedMap(stamps);
      })
      .catch(() => {
        // On error, clear loading markers (UI shows no badge instead of stuck spinner)
        setRowCounts((prev) => {
          const next = {};
          Object.keys(prev).forEach((k) => {
            next[k] = null;
          });
          return next;
        });
      });
  }, []);

  const runKarvyPreview = async (preloadedGaps = null) => {
    const token = localStorage.getItem("token");
    const preparingToast = toast.loading("Starting KARVY preview build…");
    try {
      setDownloading((prev) => ({ ...prev, mfsd205: true }));
      setKarvyModal((s) => ({ ...s, status: "downloading" }));

      const result = await downloadKarvyPreview({
        apiUrl: API_URL,
        token,
        onProgress: (msg) => toast.loading(msg, { id: preparingToast }),
      });
      if (result.gaps) {
        const gaps = result.gaps.map((g) => ({ ...g, bucket: "" }));
        setKarvyModal({ open: true, status: "gaps", gaps });
        toast.dismiss(preparingToast);
        toast.info(`${gaps.length} new Brokerage_Type(s) need to be tagged`);
        return;
      }
      toast.dismiss(preparingToast);
      toast.success(
        `${result.filename} · ${Number(result.row_count).toLocaleString()} rows · ${result.unmapped_ct} without SubBroker`,
        { duration: 6000 },
      );
      setKarvyModal({ open: false, status: "idle", gaps: [] });
    } catch (err) {
      setKarvyModal({ open: false, status: "idle", gaps: [] });
      toast.dismiss(preparingToast);
      toast.error(`Preview failed: ${err.message}`);
    } finally {
      setDownloading((prev) => ({ ...prev, mfsd205: false }));
    }
  };

  const saveKarvyBucketsAndRetry = async () => {
    const entries = karvyModal.gaps.map((g) => ({
      brokerage_type: g.brokerage_type,
      bucket: g.bucket,
    }));
    if (entries.some((e) => !e.bucket)) {
      toast.error("Every Brokerage_Type must be tagged");
      return;
    }
    try {
      setKarvyModal((s) => ({ ...s, status: "saving" }));
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/commissions/prepare/karvy/save-brokerage-type-mappings`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entries }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast.success(`Tagged ${entries.length} Brokerage_Type(s)`);
      await runKarvyPreview();
    } catch (err) {
      setKarvyModal((s) => ({ ...s, status: "gaps" }));
      toast.error(`Save failed: ${err.message}`);
    }
  };

  const openRulesPreview = async (item) => {
    if (item.usePreviewModal === "karvy_preview") {
      await runKarvyPreview();
      return;
    }
    try {
      setDownloading((prev) => ({ ...prev, [item.id]: true }));
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api/mapping-rules/json`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setRulesPreview({ rows: data.rules || [] });
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(`Failed to load ${item.label}.`);
    } finally {
      setDownloading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleDownload = async (item) => {
    if (!can(myRights, "downloads", "download")) {
      toast.error("You don't have download access. Please contact your administrator.");
      return;
    }
    if (item.usePreviewModal) {
      await openRulesPreview(item);
      return;
    }
    try {
      setDownloading((prev) => ({ ...prev, [item.id]: true }));
      toast.info(`Preparing ${item.label}...`);
      await item.action();
      toast.success(`${item.label} download started!`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Failed to download ${item.label}.`);
    } finally {
      setDownloading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleGenerate = async (item) => {
    try {
      setDownloading((prev) => ({ ...prev, [`${item.id}-generate`]: true }));
      toast.info(`Generating ${item.label}...`);
      const result = await item.generateAction();
      if (result?.success) {
        toast.success(`${item.label} generation started! Processing in background.`);
      } else {
        toast.error(result?.detail || `Failed to generate ${item.label}.`);
      }
    } catch (error) {
      console.error("Generate error:", error);
      toast.error(`Failed to generate ${item.label}.`);
    } finally {
      setDownloading((prev) => ({ ...prev, [`${item.id}-generate`]: false }));
    }
  };

  return (
    <div className="p-6" data-testid="downloads-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Download className="w-6 h-6 text-teal-600" />
          Downloads
        </h1>
        <p className="text-sm text-gray-500 mt-1">Export reports and data files</p>
      </div>

      <div className="space-y-8">
        {/* Raw Files — admin only */}
        {!isSubBroker && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <HardDrive className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Raw Files</h2>
            <span className="text-xs text-gray-400">Unprocessed source data</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {rawFiles.map((item) => (
              <DownloadCard key={item.id} item={item} downloading={downloading} onDownload={handleDownload} rowCount={item.dbName ? rowCounts[item.dbName] : undefined} lastUpdated={item.dbName ? lastUpdatedMap[item.dbName] : undefined} activeUnitsStatus={activeUnitsStatus} tmRebuildStatus={tmRebuildStatus} workerHealth={workerHealth} />
            ))}
          </div>
        </div>
        )}

        {/* Your Commission Statements — visible whenever the user has any
            commission xlsx assigned to their sub-broker code(s). Newest
            month at the top; new months appear automatically as the
            admin uploads them. Each file remains password-protected with
            the sub-broker code (build_zip in backend). */}
        {myCommissionFiles && myCommissionFiles.length > 0 && (
          <div data-testid="commissions-self-section">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Your Commission Statements
              </h2>
              <span className="text-xs text-gray-400">
                Password-protected · open with your sub-broker code
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {myCommissionFiles.map((f) => {
                const isLoading = !!downloadingComm[f.job_id];
                const codeStr = (f.files || [])
                  .map((x) => x.code)
                  .filter(Boolean)
                  .join(", ");
                const ranAt = f.run_at
                  ? (() => {
                      try {
                        return new Date(f.run_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        });
                      } catch {
                        return f.run_at;
                      }
                    })()
                  : "";
                return (
                  <div
                    key={f.job_id}
                    className="border border-amber-100 bg-amber-50/40 rounded-lg p-3 flex items-center gap-3"
                    data-testid={`commission-self-card-${f.job_id}`}
                  >
                    <div className="w-9 h-9 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <FileSpreadsheet className="w-4 h-4 text-amber-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">
                          {f.period || "Commission"}
                        </span>
                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">
                          {codeStr || "you"}
                        </span>
                        {ranAt && (
                          <span className="text-[10px] text-gray-500">
                            uploaded {ranAt}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        Open with your sub-broker code as the password.
                      </p>
                    </div>
                    <button
                      onClick={() => downloadCommissionFile(f.job_id, f.period)}
                      disabled={isLoading}
                      className="text-xs px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`commission-self-dl-${f.job_id}`}
                    >
                      {isLoading ? "…" : (<><Download className="w-3 h-3" />Download</>)}
                    </button>
                  </div>
                );
              })}
            </div>
            {myCommissionFiles.length >= 12 && (
              <p className="text-[11px] text-gray-400 mt-2">
                Showing the {myCommissionFiles.length} most recent months. Older runs auto-expire after 7 days.
              </p>
            )}
          </div>
        )}

        {/* Modified Files */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Modified Files</h2>
            <span className="text-xs text-gray-400">
              {isSubBroker ? "Scoped to your mapped clients" : "Processed & consolidated"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {modifiedFiles
              .filter((item) => !isSubBroker || SUB_BROKER_MODIFIED_IDS.has(item.id))
              .map((item) => (
                <DownloadCard key={item.id} item={item} downloading={downloading} onDownload={handleDownload} onGenerate={handleGenerate} rowCount={item.dbName ? rowCounts[item.dbName] : undefined} lastUpdated={item.dbName ? lastUpdatedMap[item.dbName] : undefined} activeUnitsStatus={activeUnitsStatus} tmRebuildStatus={tmRebuildStatus} workerHealth={workerHealth} />
              ))}
          </div>
        </div>

        {/* Categorized Other Collections — admin only. Each group here
            mirrors a Reset-DB category so operators see the same tables
            organized the same way in both places. Backend fallback in
            /api/database/export/excel adds a raw-export config for any
            collection that isn't in the allowlist, so every card here
            downloads without extra wiring. Collections already covered
            by rawFiles/modifiedFiles are excluded to avoid duplicates. */}
        {!isSubBroker && (() => {
          const KNOWN_DBS = new Set(
            [...rawFiles, ...modifiedFiles].map((it) => it.dbName).filter(Boolean)
          );
          // Same group layout as ResetDB.js.
          // NOTE: keep in sync with `/app/frontend/src/pages/ResetDB.js`.
          const otherGroups = [
            {
              title: "Account Creation Master", icon: Layers,
              subtitle: "Reference tables for new-account creation",
              iconBg: "bg-pink-500/10 text-pink-400",
              items: [
                { id: "gender", label: "Gender" },
                { id: "country", label: "Country" },
              ],
            },
            {
              title: "Master Data", icon: Layers,
              iconBg: "bg-emerald-500/10 text-emerald-400",
              items: [
                { id: "investor_master", label: "Investor Master" },
                { id: "aum_master", label: "AUM Master" },
                { id: "transaction_master", label: "Transaction Master" },
                { id: "junk_transactions", label: "Junk Transactions" },
              ],
            },
            {
              title: "AMFI Master", icon: BarChart3,
              subtitle: "Scheme catalogs, NAV history & code mappings",
              iconBg: "bg-indigo-500/10 text-indigo-400",
              items: [
                { id: "scheme_master", label: "Scheme Master" },
                { id: "scheme_data", label: "Scheme Data (AMFI)" },
                { id: "nav_master", label: "NAV Master" },
                { id: "bse_scheme_raw", label: "BSE Scheme Raw" },
                { id: "scheme_master_pulse_labs", label: "Pulse Labs Scheme Master" },
              ],
            },
            {
              title: "Commission", icon: IndianRupee,
              subtitle: "Brokerage feeds & payout reconciliation",
              iconBg: "bg-amber-500/10 text-amber-400",
              items: [
                { id: "WBR77", label: "WBR77 (CAMS)" },
                { id: "MFSD205", label: "MFSD205 (Karvy)" },
                { id: "VOLTMONEY", label: "Volt Money" },
              ],
            },
            {
              title: "Files and Chunks", icon: Archive,
              subtitle: "GridFS buckets — metadata + payload pairs",
              iconBg: "bg-gray-500/10 text-gray-400",
              items: [
                { id: "commission_invoices.files", label: "Commission Invoices (metadata)" },
                { id: "commission_invoices.chunks", label: "Commission Invoices (payload)" },
                { id: "commission_link_xlsx.files", label: "Commission Link XLSX (metadata)" },
                { id: "commission_link_xlsx.chunks", label: "Commission Link XLSX (payload)" },
              ],
            },
            {
              title: "Logs", icon: History,
              subtitle: "Job runs & communication audits",
              iconBg: "bg-violet-500/10 text-violet-400",
              items: [
                { id: "commission_email_log", label: "Commission Email Log" },
                { id: "commission_runs", label: "Commission Runs" },
              ],
            },
            {
              title: "Family & Sub-broker", icon: Users,
              subtitle: "Client relationships & attribution",
              iconBg: "bg-emerald-500/10 text-emerald-400",
              items: [
                { id: "client_master", label: "Client Master" },
                { id: "family_mapping", label: "Family Mapping" },
                { id: "subbroker_mapping", label: "Subbroker Mapping" },
                { id: "subbroker", label: "Subbroker & Associate Master", description: "All associates — 100 Associate Master columns from the latest associate details, plus status, introducer, commercials and document count" },
                { id: "transfers", label: "Transfers" },
                { id: "gifts", label: "Gifts" },
                { id: "transmissions", label: "Transmissions" },
                { id: "multi_broker_cases", label: "Multi-Broker Cases" },
              ],
            },
            {
              title: "Reconciliation & Config", icon: AlertTriangle,
              subtitle: "KYC, mismatches, logs & settings",
              iconBg: "bg-rose-500/10 text-rose-400",
              items: [
                { id: "kyc_mismatch", label: "KYC Mismatch" },
                { id: "kyc_missing", label: "KYC Missing" },
                { id: "reconciliation_jobs", label: "Reconciliation Jobs" },
                { id: "column_mapping_config", label: "Column Mapping Config" },
                { id: "upload_jobs", label: "Upload Jobs" },
                { id: "scheme_master_logs", label: "Scheme Master Logs" },
                { id: "nav_backfill_status", label: "NAV Backfill Status" },
                { id: "dashboard_cache", label: "Dashboard Cache" },
                { id: "dashboard_pan_metrics", label: "Dashboard PAN Metrics" },
                { id: "dashboard_pan_metrics_backfill", label: "Dashboard PAN Metrics Backfill" },
                { id: "dashboard_amc_details", label: "Dashboard AMC Details" },
                { id: "dashboard_aum_bracket_details", label: "Dashboard AUM Bracket Details" },
                { id: "dashboard_xirr_details", label: "Dashboard XIRR Details" },
                { id: "dashboard_cache_rejected", label: "Dashboard Cache Rejected" },
                { id: "exited_clients_full_cache", label: "Exited Clients Cache" },
                { id: "unit_mismatch_cache", label: "Unit Mismatch Cache" },
                { id: "multi_broker_cache", label: "Multi-Broker Cache" },
                { id: "txn_master_reconciliation", label: "TXN Master Reconciliation" },
                { id: "aum_active_drift_log", label: "AUM ↔ Active Drift Log" },
                { id: "email_report_tracker", label: "Email Report Tracker" },
                { id: "expected_reports", label: "Expected Reports" },
                { id: "flag_mapping_config", label: "Flag Mapping Config" },
                { id: "raw_column_order", label: "Raw Column Order" },
                { id: "portfolio_categories", label: "Portfolio Categories" },
                { id: "scan_status", label: "Scan Status" },
              ],
            },
            {
              title: "Audit & History", icon: History,
              subtitle: "Immutable logs & one-off backups",
              iconBg: "bg-violet-500/10 text-violet-400",
              items: [
                { id: "investor_audit_log", label: "Investor Audit Log" },
                { id: "investor_master_safety_backup", label: "Investor Master (Safety Backup)" },
                { id: "aum_master_safety_backup", label: "AUM Master (Safety Backup)" },
                { id: "karvy_file_logs", label: "Karvy File Logs" },
                { id: "nav_cron_logs", label: "NAV Cron Logs" },
                { id: "scheme_returns_logs", label: "Scheme Returns Logs" },
                { id: "agent_logs", label: "Agent Logs" },
                { id: "multi_broker_auto_logs", label: "Multi-Broker Auto Logs" },
                { id: "impersonation_audit", label: "Impersonation Audit" },
                { id: "download_counters", label: "Download Counters" },
                { id: "subbroker_upload_history", label: "Subbroker Upload History" },
                { id: "password_resets", label: "Password Resets" },
                { id: "active_units", label: "Active Units (legacy)" },
              ],
            },
          ];
          // Also handle any collection that appears in the DB but isn't
          // categorized — append under "Uncategorized" so nothing gets
          // silently hidden from Downloads.
          const categorizedIds = new Set();
          otherGroups.forEach((g) => g.items.forEach((it) => categorizedIds.add(it.id)));
          const uncategorized = Object.keys(rowCounts || {})
            .filter((c) =>
              !KNOWN_DBS.has(c) && !categorizedIds.has(c) &&
              !c.endsWith(".chunks") && !c.endsWith(".files") &&
              typeof rowCounts[c] === "number" && rowCounts[c] > 0
            )
            .sort();
          if (uncategorized.length > 0) {
            otherGroups.push({
              title: "Uncategorized", icon: Database, iconBg: "bg-gray-500/10 text-gray-400",
              items: uncategorized.map((id) => ({ id, label: id })),
            });
          }
          // Build a downloader factory once per render.
          const makeAction = (id) => async () => {
            if (id === "subbroker") {
              const resp = await fetch(`${API_URL}/api/subbroker/export`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
              if (!resp.ok) throw new Error("Export failed");
              const url = window.URL.createObjectURL(await resp.blob());
              const link = document.createElement("a");
              link.href = url;
              link.download = `subbroker_associate_master_${new Date().toISOString().slice(0, 10)}.xlsx`;
              document.body.appendChild(link); link.click(); document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              return;
            }
            const downloadToken = await getDownloadToken();
            window.open(`${API_URL}/api/database/export/excel?collections=${encodeURIComponent(id)}&token=${downloadToken}`, "_blank");
          };
          return otherGroups.map((group) => {
            const cards = group.items
              .filter((it) => !KNOWN_DBS.has(it.id))
              .filter((it) => typeof rowCounts[it.id] === "number" && rowCounts[it.id] > 0)
              .map((it) => ({
                id: `other-${it.id}`,
                label: it.label,
                dbName: it.id,
                icon: group.icon || Database,
                iconBg: it.iconBg || group.iconBg,
                description: it.description || "",
                action: makeAction(it.id),
              }));
            if (cards.length === 0) return null;
            const Icon = group.icon || Database;
            return (
              <div key={group.title}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon className="w-4 h-4 text-gray-500" />
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{group.title}</h2>
                  {group.subtitle && (
                    <span className="text-xs text-gray-400">{group.subtitle}</span>
                  )}
                  <span className="ml-auto text-xs text-gray-400">{cards.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                  {cards.map((item) => (
                    <DownloadCard key={item.id} item={item} downloading={downloading} onDownload={handleDownload} rowCount={rowCounts[item.dbName]} lastUpdated={lastUpdatedMap[item.dbName]} />
                  ))}
                </div>
              </div>
            );
          });
        })()}

        {/* Mapping Guides — admin only */}
        {!isSubBroker && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Mapping Guide</h2>
            <span className="text-xs text-gray-400">Reference documents</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {mappingGuides.map((item) => (
              <DownloadCard key={item.id} item={item} downloading={downloading} onDownload={handleDownload} onGenerate={handleGenerate} rowCount={item.dbName ? rowCounts[item.dbName] : undefined} lastUpdated={item.dbName ? lastUpdatedMap[item.dbName] : undefined} />
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Mapping Rules Preview Modal — shown instead of download because the Emergent
          preview iframe is sandboxed WITHOUT `allow-downloads`. Users can view and
          copy the rules as CSV to paste directly into Excel. */}
      {rulesPreview && (
        <MappingRulesModal
          rows={rulesPreview.rows}
          onClose={() => setRulesPreview(null)}
        />
      )}

      {/* KARVY Brokerage_Type gap-fill modal — appears when preview-download
          returns 409 with untagged Brokerage_Type values. */}
      {karvyModal.open && (
        <KarvyBrokerageTypeModal
          modal={karvyModal}
          setModal={setKarvyModal}
          onSave={saveKarvyBucketsAndRetry}
          onClose={() => setKarvyModal({ open: false, status: "idle", gaps: [] })}
        />
      )}
    </div>
  );
}

function KarvyBrokerageTypeModal({ modal, setModal, onSave, onClose }) {
  const busy = modal.status === "saving" || modal.status === "downloading";
  const setBucket = (idx, bucket) =>
    setModal((s) => {
      const g = s.gaps.slice();
      g[idx] = { ...g[idx], bucket };
      return { ...s, gaps: g };
    });
  const bulkSet = (bucket) =>
    setModal((s) => ({ ...s, gaps: s.gaps.map((g) => ({ ...g, bucket })) }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="karvy-bt-modal">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="border-b p-4">
          <h3 className="text-lg font-semibold">Tag new Brokerage_Type(s)</h3>
          <p className="text-sm text-slate-500 mt-1">
            {modal.gaps.length} unseen Brokerage_Type value(s) found in MFSD205_Raw.
            Tag each as <b>Zero</b> (Kinntegra_Share = 0) or <b>Trail</b> (Kinntegra_Share picked from SubBroker rule).
          </p>
        </div>
        <div className="px-4 py-2 border-b flex items-center gap-2 text-xs">
          <span className="text-slate-500">Bulk set:</span>
          <button className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => bulkSet("zero")} disabled={busy}>All → Zero</button>
          <button className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => bulkSet("trail")} disabled={busy}>All → Trail</button>
        </div>
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2">Brokerage_Type</th>
                <th className="text-right px-4 py-2">Rows</th>
                <th className="text-left px-4 py-2">Bucket</th>
              </tr>
            </thead>
            <tbody>
              {modal.gaps.map((g, i) => (
                <tr key={g.brokerage_type} className="border-t">
                  <td className="px-4 py-2 font-mono">{g.brokerage_type}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{g.row_count.toLocaleString()}</td>
                  <td className="px-4 py-2">
                    <select
                      value={g.bucket}
                      onChange={(e) => setBucket(i, e.target.value)}
                      disabled={busy}
                      data-testid={`karvy-bt-select-${i}`}
                      className="rounded border border-slate-300 px-2 py-1 text-sm bg-white"
                    >
                      <option value="">— pick —</option>
                      <option value="zero">Zero (Kinntegra_Share = 0)</option>
                      <option value="trail">Trail (use SB rule)</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t p-4 flex justify-end gap-2">
          <button className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="rounded bg-teal-600 px-3 py-1.5 text-sm text-white hover:bg-teal-700 disabled:opacity-50"
            onClick={onSave}
            disabled={busy || modal.gaps.some((g) => !g.bucket)}
            data-testid="karvy-bt-save"
          >
            {busy ? "Working…" : "Save & Generate Preview"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MappingRulesModal({ rows, onClose }) {
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("all");

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "cams") return r.source === "CAMS_WBR2";
    if (filter === "karvy") return r.source === "KARVY_MFSD201";
    if (filter === "cross") return !["CAMS_WBR2", "KARVY_MFSD201"].includes(r.source);
    return true;
  });

  const toCSV = (data) => {
    const cols = ["source", "txn_type", "txn_flag", "flag", "description", "treatment", "rule_name"];
    const header = ["Source", "Txn Type", "Txn Flag", "Flag", "Description", "Treatment", "Rule Name"];
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    data.forEach((r) => lines.push(cols.map((c) => esc(r[c])).join(",")));
    return lines.join("\n");
  };

  const copyCSV = async () => {
    try {
      await navigator.clipboard.writeText(toCSV(filtered));
      setCopied(true);
      toast.success(`Copied ${filtered.length} rows as CSV — paste into Excel (Ctrl+V)`);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Clipboard access denied — select the table text manually");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="mapping-rules-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Unique Flags, Txn Types &amp; Sources — Live Mapping Rules</h3>
            <p className="text-xs text-gray-500 mt-0.5">{filtered.length} of {rows.length} rules · programmatic (in-code)</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1"
              data-testid="mapping-rules-filter"
            >
              <option value="all">All sources</option>
              <option value="cams">CAMS_WBR2</option>
              <option value="karvy">KARVY_MFSD201</option>
              <option value="cross">Cross-source</option>
            </select>
            <button
              onClick={copyCSV}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-amber-500 text-white hover:bg-amber-600"
              data-testid="mapping-rules-copy-csv"
            >
              <Copy className="w-3 h-3" />
              {copied ? "Copied!" : "Copy as CSV"}
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100"
              data-testid="mapping-rules-close"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Source</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Txn Type</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Txn Flag</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Flag</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Description</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Treatment</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Rule</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} className={i % 2 ? "bg-white" : "bg-gray-50/40"}>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-gray-500">{r.source}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.txn_type || "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.txn_flag || "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.flag || "—"}</td>
                  <td className="px-3 py-1.5">{r.description || "—"}</td>
                  <td className="px-3 py-1.5 text-[11px]">{r.treatment || "—"}</td>
                  <td className="px-3 py-1.5 text-gray-500 text-[11px]">{r.rule_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t px-5 py-2 text-[11px] text-gray-500 bg-gray-50">
          Tip: Downloads are blocked by this preview iframe's sandbox. Use <b>Copy as CSV</b> and paste into Excel (Ctrl+V) to get the same data.
        </div>
      </div>
    </div>
  );
}
